import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAtom } from 'jotai';
import ReactEcharts from 'echarts-for-react';
import { Tooltip, Button, Popover, Space, Cascader, Row, Col, Switch, Slider, InputNumber, Select, Typography, Descriptions } from 'antd';
import { UndoOutlined, DownloadOutlined, PlusOutlined, MinusOutlined, EyeOutlined, DotChartOutlined, BranchesOutlined } from '@ant-design/icons';
import { cloneDeep, indexOf } from 'lodash';
import { getWavenumbers } from '../utils/wavenumbers';
import { deviceInfoState, userNameState, userGroupsState, mapScalePercentFilterState, selectedSubstanceForMapFilterState, mapFilterByMLState } from '../stateManagement/commonState';
import { darkReferenceSpectrumState } from '../stateManagement/processState.js';
import { fetchReferenceSubstanceNames, fetchReferenceSpectrum } from '../utils/dbHelpers.js';
import { substanceArray as mlSubstanceArray } from '../utils/substancesAndColors';
import { smooth } from '../utils/modifiedSincSmoother.js';
import { ASPLS } from '../utils/baseline_aspls.js';
import { calibrateRamanIntensity, removeBadPixels, interpolate, calculateR2 } from '../utils/helpers';
import SpectrumValidation from './SpectrumValidation';

const { Text } = Typography;

const SpectraLineChart = ({ linechartSpectrum, updateWavenumberFilter }) => {
    const [username] = useAtom(userNameState);
    const [userGroups] = useAtom(userGroupsState);
    const [deviceInfo] = useAtom(deviceInfoState);
    let wavenumbers = deviceInfo ? getWavenumbers(500, deviceInfo.wavelength_calibration, deviceInfo.excitation_wavelength) : [];
    let standardWavenumbers = [...Array(2324 - 225 + 1)].map((_, i) => 225 + i);
    const spectrumLineChartRef = useRef(null);
    const wavenumberFilterIndexRef = useRef(null);
    const [referenceSubstanceCascaderOptions, setReferenceSubstanceCascaderOptions] = useState([]);
    const [currentReferenceSubstances, setCurrentReferenceSubstances] = useState([]);
    const [prevReferenceSeriesCount, setPrevReferenceSeriesCount] = useState(0);
    const [selectedReferenceSubstances, setSelectedReferenceSubstances] = useState([]);
    const [darkReferenceSpectrum] = useAtom(darkReferenceSpectrumState);
    const [legendSelected, setLegendSelected] = useState({
        'Raw': false,
        'Dark Reference': false,
        'Dark Subtracted': false,
        'Bad Pixel Corrected': false,
        'Smoothed': false,
        'Baseline': false,
        'Baseline Corrected': false,
        'Intensity Corrected': false,
        'Interpolated': false,
        'Processed': true,
        'Normalized': false,
    });
    const deviceInfoRef = useRef(null);
    const [reprocessSpectrum, setReprocessSpectrum] = useState(false);
    const [basicSeries, setBasicSeries] = useState([
        {
            id: 'rawSpectrum',
            name: 'Raw',
            data: new Array(500).fill(null).map((e, i) => [wavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
        {
            id: 'processedSpectrum',
            name: 'Processed',
            data: new Array(2100).fill(null).map((e, i) => [standardWavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
    ]);
    const [processSeries, setProcessSeries] = useState([
        {
            id: 'darkReferenceSpectrum',
            name: 'Dark Reference',
            data: new Array(500).fill(null).map((e, i) => [wavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
        {
            id: 'darkSubtractedSpectrum',
            name: 'Dark Subtracted',
            data: new Array(500).fill(null).map((e, i) => [wavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
        {
            id: 'badPixelCorrectedSpectrum',
            name: 'Bad Pixel Corrected',
            data: new Array(500).fill(null).map((e, i) => [wavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
        {
            id: 'smoothedSpectrum',
            name: 'Smoothed',
            data: new Array(500).fill(null).map((e, i) => [wavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
        {
            id: 'baseline',
            name: 'Baseline',
            data: new Array(500).fill(null).map((e, i) => [wavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
        {
            id: 'baselineCorrectedSpectrum',
            name: 'Baseline Corrected',
            data: new Array(500).fill(null).map((e, i) => [wavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
        {
            id: 'intensityCorrectedSpectrum',
            name: 'Intensity Corrected',
            data: new Array(500).fill(null).map((e, i) => [wavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
        {
            id: 'interpolatedSpectrum',
            name: 'Interpolated',
            data: new Array(2100).fill(null).map((e, i) => [standardWavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
        {
            id: 'normalizedSpectrum',
            name: 'Normalized',
            data: new Array(2100).fill(null).map((e, i) => [standardWavenumbers[i], 0]),
            type: 'line',
            showSymbol: false,
        },
    ]);
    const [filterSeries, setFilterSeries] = useState([
        {
            id: 'wavenumberFilterMarkline',
            silent: true,
            type: 'line',
            markLine: {
                silent: true,
                lineStyle: {
                    color: '#333'
                },
                symbol: ['none', 'none'],
                data: [],
                animation: false,
                label: {
                    show: true,
                    formatter: (params) => {
                        return `${params.value}`;
                    }
                }
            }
        },
    ]);
    const [referenceSpectrumSeries, setReferenceSpectrumSeries] = useState([]);
    const [marklineSeries, setMarklineSeries] = useState([]);
    const [lineChartOption, setLineChartOption] = useState({
        legend: {
            data: [],
            bottom: 0,
            selected: legendSelected,
            type: 'scroll',
            icon: 'circle',
        },
        tooltip: {
            trigger: 'axis',
        },
        dataZoom: [
            {
                type: 'inside'
            }
        ],
        xAxis: {
            type: 'value',
            data: standardWavenumbers,
            min: standardWavenumbers[0],
            max: standardWavenumbers[standardWavenumbers.length - 1],
        },
        yAxis: {
            type: 'value',
            scale: true,
        },
        grid: {
            top: '20px',
            right: '15px',
            bottom: '30px',
            left: '15px',
            containLabel: true,
        },
        series: [
            ...basicSeries,
            ...processSeries,
            ...filterSeries,
            ...referenceSpectrumSeries,
            ...marklineSeries
        ],
    });

    const [scalePercent, setScalePercent] = useState({});
    const [peakIndices, setPeakIndices] = useState({});
    const [mapScalePercentFilter, setMapScalePercentFilter] = useAtom(mapScalePercentFilterState);
    const [selectedSubstanceForMapFilter, setSelectedSubstanceForMapFilter] = useAtom(selectedSubstanceForMapFilterState);
    const [mapFilterByML, setMapFilterByML] = useAtom(mapFilterByMLState);
    const [combinedSpectrumSeries, setCombinedSpectrumSeries] = useState([]);
    const [showCombinedSpectrum, setShowCombinedSpectrum] = useState(true);
    const [combinedSpectrumR2, setCombinedSpectrumR2] = useState(null);
    const [r2Contributions, setR2Contributions] = useState({});
    const maxReferenceIntensitiesRef = useRef({});

    useEffect(() => {
        getReferenceSubstanceNames();
    }, []);

    useEffect(() => {
        deviceInfoRef.current = deviceInfo
    }, [deviceInfo]);

    useEffect(() => {
        let dataPoint = { ...linechartSpectrum };
        if (dataPoint?.index === null) { return; }
        wavenumbers = deviceInfo ? getWavenumbers(deviceInfo.active_pixels, deviceInfo.wavelength_calibration, deviceInfo.excitation_wavelength) : [];
        if (reprocessSpectrum && deviceInfoRef.current) {
            if (darkReferenceSpectrum) {
                const halfMinRawSpectrumIntensity = Math.min(...dataPoint.rawSpectrum) / 2;
                dataPoint["darkSubtractedSpectrum"] = dataPoint.rawSpectrum.map((e, i) => e - darkReferenceSpectrum[i]);
                const minDarkSubtracted = Math.min(...dataPoint["darkSubtractedSpectrum"]);
                dataPoint["darkSubtractedSpectrum"] = dataPoint["darkSubtractedSpectrum"].map(e => e + (halfMinRawSpectrumIntensity - minDarkSubtracted));
            }
            dataPoint["badPixelCorrectedSpectrum"] = removeBadPixels(dataPoint.darkSubtractedSpectrum ? dataPoint.darkSubtractedSpectrum : dataPoint.rawSpectrum, deviceInfoRef.current.bad_pixels);
            dataPoint["smoothedSpectrum"] = smooth(dataPoint.badPixelCorrectedSpectrum, null);
            dataPoint["baseline"] = ASPLS({ data: dataPoint.badPixelCorrectedSpectrum, x_data: wavenumbers });
            dataPoint.baselineCorrectedSpectrum = dataPoint.badPixelCorrectedSpectrum.map((e, i) => Math.max(0, e - dataPoint["baseline"][i]));
            dataPoint["intensityCorrectedSpectrum"] = calibrateRamanIntensity(dataPoint.baselineCorrectedSpectrum, deviceInfoRef.current.intensity_calibration);
            dataPoint["interpolatedSpectrum"] = interpolate(standardWavenumbers, wavenumbers, dataPoint["intensityCorrectedSpectrum"]);
            dataPoint["processedSpectrum"] = dataPoint["interpolatedSpectrum"];
            let q = Math.hypot(...dataPoint["interpolatedSpectrum"]);
            dataPoint["normalizedSpectrum"] = dataPoint["interpolatedSpectrum"].map((e, i) => e / q);
        }
        setLineChartOption(prevOption => {
            let newOption = cloneDeep(prevOption);
            newOption.xAxis.min = wavenumbers[0]
            newOption.xAxis.max = wavenumbers[wavenumbers.length - 1]
            return newOption
        });
        setBasicSeries(prev => {
            let newBasicSeries = cloneDeep(prev);
            newBasicSeries.find(obj => obj.id === "rawSpectrum").data = dataPoint.rawSpectrum.map((e, i) => [wavenumbers[i], e]);
            newBasicSeries.find(obj => obj.id === "processedSpectrum").data = dataPoint.processedSpectrum.map((e, i) => [standardWavenumbers[i], e]);
            return newBasicSeries
        });
        setProcessSeries(prev => {
            let newProcessSeries = cloneDeep(prev);
            newProcessSeries.find(obj => obj.id === "darkReferenceSpectrum").data = darkReferenceSpectrum?.map((e, i) => [wavenumbers[i], e]);
            newProcessSeries.find(obj => obj.id === "darkSubtractedSpectrum").data = dataPoint.darkSubtractedSpectrum?.map((e, i) => [wavenumbers[i], e]);
            newProcessSeries.find(obj => obj.id === "badPixelCorrectedSpectrum").data = dataPoint.badPixelCorrectedSpectrum?.map((e, i) => [wavenumbers[i], e]);
            newProcessSeries.find(obj => obj.id === "smoothedSpectrum").data = dataPoint.smoothedSpectrum?.map((e, i) => [wavenumbers[i], e]);
            newProcessSeries.find(obj => obj.id === "baseline").data = dataPoint.baseline?.map((e, i) => [wavenumbers[i], e]);
            newProcessSeries.find(obj => obj.id === "baselineCorrectedSpectrum").data = dataPoint.baselineCorrectedSpectrum?.map((e, i) => [wavenumbers[i], e]);
            newProcessSeries.find(obj => obj.id === "intensityCorrectedSpectrum").data = dataPoint.intensityCorrectedSpectrum?.map((e, i) => [wavenumbers[i], e]);
            newProcessSeries.find(obj => obj.id === "interpolatedSpectrum").data = dataPoint.interpolatedSpectrum?.map((e, i) => [standardWavenumbers[i], e]);
            newProcessSeries.find(obj => obj.id === "normalizedSpectrum").data = dataPoint.normalizedSpectrum?.map((e, i) => [standardWavenumbers[i], e]);
            return newProcessSeries
        });
        updateAllScalePercents();
    }, [deviceInfo, linechartSpectrum, reprocessSpectrum, darkReferenceSpectrum]);

    useEffect(() => {
        setLineChartOption(prevOption => ({
            ...prevOption,
            legend: {
                ...prevOption.legend,
                data: [
                    ...basicSeries.map(e => e?.name).filter(name => name != null),
                    ...(showCombinedSpectrum ? combinedSpectrumSeries.map(e => e?.name).filter(name => name != null) : []),
                    ...(reprocessSpectrum ? processSeries.map(e => e?.name).filter(name => name != null) : []),
                    ...(marklineSeries.length ? [] : filterSeries.map(e => e?.name).filter(name => name != null)),
                    ...referenceSpectrumSeries.map(e => e?.name).filter(name => name != null),
                    ...marklineSeries.map(e => e?.name).filter(name => name != null),
                ]
            },
            series: [
                ...basicSeries,
                ...(showCombinedSpectrum ? combinedSpectrumSeries : []),
                ...(reprocessSpectrum ? processSeries : []),
                ...(marklineSeries.length ? [] : filterSeries),
                ...referenceSpectrumSeries,
                ...marklineSeries,
            ]
        }));
        setLegendSelected(prev => {
            let newLegendSelected = { ...prev };
            // Add new entries with true, but don't overwrite existing ones
            [
                ...basicSeries,
                ...(showCombinedSpectrum ? combinedSpectrumSeries : []),
                ...(reprocessSpectrum ? processSeries : []),
                ...(marklineSeries.length ? [] : filterSeries),
                ...referenceSpectrumSeries,
                ...marklineSeries
            ].forEach(e => {
                if (e?.name && !(e.name in newLegendSelected)) {
                    newLegendSelected[e.name] = true;
                }
            });
            // Remove entries that are no longer in any series
            Object.keys(newLegendSelected).forEach(key => {
                if (![
                    ...basicSeries,
                    ...(showCombinedSpectrum ? combinedSpectrumSeries : []),
                    ...(reprocessSpectrum ? processSeries : []),
                    ...(marklineSeries.length ? [] : filterSeries),
                    ...referenceSpectrumSeries,
                    ...marklineSeries
                ].some(e => e?.name === key)) {
                    delete newLegendSelected[key];
                }
            });
            return newLegendSelected;
        });
    }, [basicSeries, processSeries, filterSeries, referenceSpectrumSeries, marklineSeries, reprocessSpectrum, showCombinedSpectrum, combinedSpectrumSeries]);

    useEffect(() => {
        const processedSpectrum = (basicSeries.find(e => e.id === 'processedSpectrum')?.data || []).map(e => e[1]);
        let combinedSpectrum = new Array(standardWavenumbers.length).fill(0);
        for (let i = 0; i < referenceSpectrumSeries.length; i++) {
            const referenceSpectrumData = referenceSpectrumSeries[i].data.map(e => e[1]);
            combinedSpectrum = combinedSpectrum.map((value, index) => value + referenceSpectrumData[index]);
        }
        const combinedR2 = calculateR2(processedSpectrum, combinedSpectrum);
        setCombinedSpectrumR2(combinedR2);
        setCombinedSpectrumSeries([{
            id: 'combinedSpectrum',
            name: 'Combined',
            data: combinedSpectrum.map((intensity, i) => [standardWavenumbers[i], intensity]),
            type: 'line',
            showSymbol: false,
        }]);
        setShowCombinedSpectrum(referenceSpectrumSeries.length > 1 ? true : false);
    }, [basicSeries, referenceSpectrumSeries]);

    useEffect(() => {
        setLineChartOption(prevOption => {
            let newOption = cloneDeep(prevOption);
            newOption.legend.selected = { ...legendSelected };
            return newOption
        });
    }, [legendSelected]);

    useEffect(() => {
        const r2Contributions = {};
        maxReferenceIntensitiesRef.current = {};
        for (let i = 0; i < referenceSpectrumSeries.length; i++) {
            const referenceSpectrum = referenceSpectrumSeries[i].data.map(e => e[1]);
            maxReferenceIntensitiesRef.current[referenceSpectrumSeries[i].name] = Math.max(...referenceSpectrum);
            const subtractedSpectrum = combinedSpectrumSeries[0].data.map((e, idx) => e[1] - referenceSpectrum[idx]);
            const processedSpectrum = (basicSeries.find(e => e.id === 'processedSpectrum')?.data || []).map(e => e[1]);
            r2Contributions[referenceSpectrumSeries[i].name] = combinedSpectrumR2 - calculateR2(processedSpectrum, subtractedSpectrum);
        }
        setR2Contributions(r2Contributions);
    }, [basicSeries, combinedSpectrumR2, referenceSpectrumSeries, combinedSpectrumSeries]);

    const getReferenceSubstanceNames = async () => {
        let referenceSubstanceNames = await fetchReferenceSubstanceNames();
        setReferenceSubstanceCascaderOptions(referenceSubstanceNames.map(e => ({ value: e.substance_description, label: e.substance_description })));
    }

    const updateWavenumberMarkline = wavenumber => {
        setFilterSeries(prev => {
            let newFilterSeries = cloneDeep(prev);
            newFilterSeries.find(obj => obj.id === "wavenumberFilterMarkline").markLine.data = wavenumber ? [{ xAxis: wavenumber }] : [];
            return newFilterSeries
        });
    }

    const downloadSpectrum = (seriesName, seriesData) => {
        const csvContent = seriesData
            .map((e) => e.join(","))
            .join("\n");
        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `scatr_${linechartSpectrum.serial === null ? '' : `serial_${linechartSpectrum.serial}_`}index_${linechartSpectrum.index}_${seriesName.replace(/\s/g, '_').toLowerCase()}.csv`);
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(link.href);
    }

    useEffect(() => {
        setPrevReferenceSeriesCount(lineChartOption.series.length);
        if (spectrumLineChartRef && spectrumLineChartRef.current) {
            const notMerge = prevReferenceSeriesCount > lineChartOption.series.length;
            spectrumLineChartRef.current.getEchartsInstance().setOption(lineChartOption, notMerge);
        }
    }, [lineChartOption]);

    useEffect(() => {
        if (spectrumLineChartRef && spectrumLineChartRef.current) {
            let chart = spectrumLineChartRef.current.getEchartsInstance();
            chart.setOption(lineChartOption, false);
            chart.getZr().on('click', function (event) {
                let pointInPixel = [event.offsetX, event.offsetY];
                let pointInGrid = chart.convertFromPixel('grid', pointInPixel);
                if (pointInPixel[1] > 250 || pointInGrid[1] < 0) { return }
                let wavenumber = standardWavenumbers.reduce(function (prev, curr) {
                    return (Math.abs(curr - pointInGrid[0]) < Math.abs(prev - pointInGrid[0]) ? curr : prev);
                });
                updateWavenumberMarkline(wavenumber);
                wavenumberFilterIndexRef.current = indexOf(standardWavenumbers, wavenumber);
                updateWavenumberFilter(indexOf(standardWavenumbers, wavenumber));
                setSelectedSubstanceForMapFilter(null);
                setMapFilterByML(false);
            });
            chart.on('legendselectchanged', (params) => {
                setLegendSelected(params.selected);
            });
        }
    }, []);

    const handleSelectedReferenceChange = async (selectedReferenceSubstances) => {
        updateScalePercentKeys(selectedReferenceSubstances.flat());
        syncPeakIndicesWithSubstances(selectedReferenceSubstances.flat());
        const newCurrentReferenceSubstances = selectedReferenceSubstances.map(e => ({
            reference_substance: e[0],
            processed_spectrum: currentReferenceSubstances.find(f => f.reference_substance === e[0])?.processed_spectrum || [],
            integration_time: currentReferenceSubstances.find(f => f.reference_substance === e[0])?.integration_time || null,
        }));
        for (let i = 0; i < newCurrentReferenceSubstances.length; i++) {
            if (newCurrentReferenceSubstances[i].processed_spectrum.length === 0) {
                let referenceSpectrumData = await fetchReferenceSpectrum(newCurrentReferenceSubstances[i].reference_substance, linechartSpectrum.integrationTime || 2000);
                newCurrentReferenceSubstances[i] = { ...newCurrentReferenceSubstances[i], ...referenceSpectrumData };
            }
        }
        setCurrentReferenceSubstances(newCurrentReferenceSubstances);
        setSelectedSubstanceForMapFilter(null);
        setMapFilterByML(false);
    }

    useEffect(() => {
        handleSelectedReferenceChange(selectedReferenceSubstances);
    }, [selectedReferenceSubstances]);

    useEffect(() => {
        setReferenceSpectrumSeries(currentReferenceSubstances.map((e, i) => ({
            id: e.reference_substance,
            name: e.reference_substance,
            data: e.processed_spectrum.map((intensity, i) => [standardWavenumbers[i], intensity * (scalePercent[e.reference_substance] / 100)]),
            type: 'line',
            showSymbol: false,
        })));
    }, [currentReferenceSubstances, scalePercent]);

    useEffect(() => {
        const newPeakIndices = { ...peakIndices };
        for (let i = 0; i < currentReferenceSubstances.length; i++) {
            if (!newPeakIndices[currentReferenceSubstances[i].reference_substance]) {
                newPeakIndices[currentReferenceSubstances[i].reference_substance] = findPeaks(currentReferenceSubstances[i].processed_spectrum);
            }
        }
        setPeakIndices(newPeakIndices);
    }, [currentReferenceSubstances]);

    const relevantPeaks = useMemo(() => {
        return peakIndices[selectedSubstanceForMapFilter] || [];
    }, [selectedSubstanceForMapFilter, peakIndices]);

    const newFilter = useMemo(() => {
        return relevantPeaks.filter(peak => peak.selected);
    }, [relevantPeaks]);

    useEffect(() => {
        if (newFilter.length === 0 && mapScalePercentFilter.length === 0) { return; }
        setMapScalePercentFilter(newFilter);
    }, [newFilter]);

    const spectrumLineChart = useMemo(() =>
        <ReactEcharts
            option={lineChartOption}
            ref={spectrumLineChartRef}
            style={{ width: "100%", height: '100%' }}
        />,
        []
    );

    const filter = (inputValue, path) => path.some((option) => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

    const updateScalePercentKeys = (substanceNameArray) => {
        setScalePercent((prevScalePercent) => {
            const updatedScales = { ...prevScalePercent };

            substanceNameArray.forEach((name) => {
                if (!prevScalePercent.hasOwnProperty(name)) {
                    updatedScales[name] = 100;
                }
            });

            Object.keys(prevScalePercent).forEach((key) => {
                if (!substanceNameArray.includes(key)) {
                    delete updatedScales[key];
                }
            });

            return updatedScales;
        });
    };

    const syncPeakIndicesWithSubstances = (substanceNameArray) => {
        setPeakIndices((prevPeakIndices) => {
            const updatedPeakIndices = { ...prevPeakIndices };
            Object.keys(prevPeakIndices).forEach((key) => {
                if (!substanceNameArray.includes(key)) {
                    delete updatedPeakIndices[key];
                }
            });
            return updatedPeakIndices;
        });
    };

    const findPeaks = (spectrum) => {
        const peakIndices = [];
        for (let i = 2; i < spectrum.length; i++) {
            if (spectrum[i - 1] > 200 && spectrum[i - 1] > spectrum[i - 2] && spectrum[i - 1] > spectrum[i]) {
                peakIndices.push({ 'index': i - 1, 'intensity': spectrum[i - 1], 'wavenumber': standardWavenumbers[i - 1], 'selected': false });
            }
        }
        return peakIndices.sort((a, b) => b.intensity - a.intensity);
    }

    const updateScalePercent = (referenceSubstance, selectedPeakIndices) => {
        const peaks = peakIndices[referenceSubstance] || [];
        if (peaks.length === 0) return;
        let minScalePercent = 100;
        const dataPointProcessedSpectrum = linechartSpectrum.processedSpectrum;
        const referenceSpectrum = currentReferenceSubstances.find(e => e.reference_substance === referenceSubstance)?.processed_spectrum;
        for (let i = 0; i < selectedPeakIndices.length; i++) {
            const scaleFactor = dataPointProcessedSpectrum[selectedPeakIndices[i]] / referenceSpectrum[selectedPeakIndices[i]];
            const scalePercent = scaleFactor * 100;
            if (i === 0) { minScalePercent = scalePercent; }
            else {
                minScalePercent = Math.min(minScalePercent, scalePercent);
            }
        }
        setScalePercent(prevScalePercent => ({
            ...prevScalePercent,
            [referenceSubstance]: minScalePercent
        }));
    }

    const updateAllScalePercents = () => {
        const newScalePercents = {};
        const dataPointProcessedSpectrum = linechartSpectrum.processedSpectrum;
        Object.keys(peakIndices).forEach(substance => {
            const peaks = peakIndices[substance].filter(e => e.selected).map(e => e.index) || [];
            if (peaks.length === 0) {
                newScalePercents[substance] = scalePercent[substance];
                return;
            };
            let minScalePercent = 100;
            const referenceSpectrum = currentReferenceSubstances.find(e => e.reference_substance === substance)?.processed_spectrum;
            for (let i = 0; i < peaks.length; i++) {
                const scaleFactor = dataPointProcessedSpectrum[peaks[i]] / referenceSpectrum[peaks[i]];
                const scalePercent = scaleFactor * 100;
                if (i === 0) { minScalePercent = scalePercent; }
                else {
                    minScalePercent = Math.min(minScalePercent, scalePercent);
                }
            }
            newScalePercents[substance] = minScalePercent;
        });
        setScalePercent(newScalePercents);
    }

    const updateMarklineSeries = (xAxisValues) => {
        setMarklineSeries([{
            silent: true,
            type: 'line',
            markLine: {
                silent: true,
                lineStyle: {
                    color: '#333'
                },
                label: {
                    show: true,
                    formatter: (params) => {
                        return `${params.dataIndex + 1}`;
                    }
                },
                symbol: ['none', 'none'],
                data: xAxisValues.map(x => ({ xAxis: x })),
                animation: false,
            }
        }]);
    };

    const clearSingleWavenumberFilter = () => {
        wavenumberFilterIndexRef.current = null;
        updateWavenumberFilter(null);
        updateWavenumberMarkline(null);
    }

    return (
        <>
            <Row style={{ margin: '15px 15px 0px 15px' }}>
                <Col>
                    <Popover placement="topRight" title={`Download Spectrum`} content={(
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {basicSeries.map(series => {
                                if (!series.name) { return null }
                                return (
                                    <Button key={series.name} type="default" block onClick={() => downloadSpectrum(series.name, series.data)} disabled={!series.data}>
                                        {series.name}
                                    </Button>
                                )
                            })}
                            {
                                username === 'admin' ? (
                                    processSeries.map(series => {
                                        if (!series.name) return null;
                                        return (
                                            <Button
                                                key={series.name}
                                                type="default"
                                                block
                                                onClick={() => downloadSpectrum(series.name, series.data)}
                                                disabled={!series.data}
                                            >
                                                {series.name}
                                            </Button>
                                        );
                                    })
                                ) : null
                            }
                        </Space>
                    )} trigger="click">
                        <Button icon={<DownloadOutlined />} disabled={linechartSpectrum.index === null ? true : false}></Button>
                    </Popover>
                </Col>
                <Col flex="auto">
                    <Cascader
                        multiple
                        maxTagCount='responsive'
                        style={{ width: '100%', padding: '0 3px 0 3px' }}
                        options={referenceSubstanceCascaderOptions}
                        onChange={e => setSelectedReferenceSubstances(e)}
                        placeholder="Add reference spectra"
                        showSearch={{ filter }}
                    />
                </Col>
                <Col>
                    <Tooltip placement="topRight" title={`Clear Filter`}>
                        <Button icon={<UndoOutlined />} onClick={() => { clearSingleWavenumberFilter() }} disabled={wavenumberFilterIndexRef.current === null}></Button>
                    </Tooltip>
                </Col>
                {(username === 'admin' || username === 'reference') ?
                    <Col>
                        <SpectrumValidation
                            linechartSpectrum={linechartSpectrum}
                            expectedClassifications={selectedReferenceSubstances.flat()}
                            combinedSpectrumR2={combinedSpectrumR2}
                            r2Contributions={r2Contributions}
                            maxReferenceIntensities={maxReferenceIntensitiesRef.current}
                        />
                    </Col>
                    : null}
            </Row >
            <div style={{ height: '300px', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 24, right: 18, zIndex: 2 }}>
                    {/* {(username && userGroups.length && userGroups.includes('super_user')) ? */}
                    {username === 'admin' ?
                        <Switch
                            checkedChildren="Process"
                            unCheckedChildren="Process"
                            size='small'
                            onChange={value => setReprocessSpectrum(value)}
                        /> : null}
                    <Text
                        style={{ display: 'block', textAlign: 'center' }}
                    >
                        R²: {combinedSpectrumR2?.toFixed(3)}
                    </Text>
                </div>
                {linechartSpectrum.index !== null ?
                    <Text style={{ position: `absolute`, left: `50%`, transform: `translateX(-50%)`, top: `24px`, zIndex: 2 }}>
                        Index: {linechartSpectrum.index}
                    </Text> : null}
                {spectrumLineChart}
            </div>
            <div style={{ padding: '8px 0' }}>
                {
                    currentReferenceSubstances.map((e, i) => (
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '4px' }}
                            onMouseEnter={() => {
                                updateMarklineSeries(peakIndices[e.reference_substance]?.filter(peak => peak.selected).map(peak => peak.wavenumber));
                            }}
                            onMouseLeave={() => { setMarklineSeries([]) }}
                        >
                            <p style={{ display: 'inline-block', width: '150px', margin: '0 4px 0 0', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', textAlign: 'center' }}>{e.reference_substance}</p>
                            <Select
                                mode="multiple"
                                allowClear
                                style={{ flex: 1, minWidth: 0 }}
                                maxTagCount='responsive'
                                placeholder="Select peaks to fit"
                                value={peakIndices[e.reference_substance]?.filter(peak => peak.selected).map(peak => peak.index) || []}
                                onChange={(selectedPeakIndices) => {
                                    setPeakIndices((prev) => {
                                        const peaks = prev[e.reference_substance];
                                        if (!peaks || peaks.length === 0) return prev;
                                        const updatedPeaks = peaks.map((peak, i) => ({
                                            ...peak,
                                            selected: selectedPeakIndices.includes(peak.index)
                                        }));
                                        updateMarklineSeries(updatedPeaks.filter(peak => peak.selected).map(peak => peak.wavenumber));
                                        updateScalePercent(e.reference_substance, selectedPeakIndices);
                                        return {
                                            ...prev,
                                            [e.reference_substance]: updatedPeaks,
                                        };
                                    });
                                }}
                                options={peakIndices[e.reference_substance]?.map((peak) => ({
                                    label: peak.wavenumber,
                                    value: peak.index,
                                })) || []}
                                onClear={() => {
                                    setPeakIndices((prev) => {
                                        const peaks = prev[e.reference_substance];
                                        if (!peaks || peaks.length === 0) return prev;
                                        const updatedPeaks = peaks.map((peak) => ({ ...peak, selected: false }));
                                        updateMarklineSeries(updatedPeaks.filter(peak => peak.selected).map(peak => peak.wavenumber));
                                        updateScalePercent(e.reference_substance, updatedPeaks.filter(peak => peak.selected).map(peak => peak.index));
                                        setMapFilterByML(false);
                                        return {
                                            ...prev,
                                            [e.reference_substance]: updatedPeaks,
                                        };
                                    });
                                }}
                            />
                            <Button
                                icon={<MinusOutlined />}
                                onClick={() => {
                                    setPeakIndices((prev) => {
                                        const peaks = prev[e.reference_substance];
                                        if (!peaks || peaks.length === 0) return prev;
                                        const lastSelectedIndex = peaks.findLastIndex((peak) => peak.selected);
                                        if (lastSelectedIndex === -1) return prev;
                                        const updatedPeaks = peaks.map((peak, i) =>
                                            i === lastSelectedIndex ? { ...peak, selected: false } : peak
                                        );
                                        updateScalePercent(e.reference_substance, updatedPeaks.filter(peak => peak.selected).map(peak => peak.index));
                                        updateMarklineSeries(updatedPeaks.filter(peak => peak.selected).map(peak => peak.wavenumber));
                                        return {
                                            ...prev,
                                            [e.reference_substance]: updatedPeaks,
                                        };
                                    });
                                }}
                            />
                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setPeakIndices((prev) => {
                                        const peaks = prev[e.reference_substance];
                                        if (!peaks || peaks.length === 0) return prev;
                                        const earliestUnselectedIndex = peaks.findIndex((peak) => !peak.selected);
                                        if (earliestUnselectedIndex === -1) return prev;
                                        const updatedPeaks = peaks.map((peak, i) =>
                                            i === earliestUnselectedIndex ? { ...peak, selected: true } : peak
                                        );
                                        updateScalePercent(e.reference_substance, updatedPeaks.filter(peak => peak.selected).map(peak => peak.index));
                                        updateMarklineSeries(updatedPeaks.filter(peak => peak.selected).map(peak => peak.wavenumber));
                                        return {
                                            ...prev,
                                            [e.reference_substance]: updatedPeaks,
                                        };
                                    });

                                }}
                            />
                            <Popover title={''} popupStyle={{ width: '40px', margin: 'auto' }} content={(
                                <Slider
                                    min={0}
                                    vertical
                                    tooltip={{ open: false }}
                                    style={{ height: '120px', margin: '8px auto' }}
                                    onChange={newValue => {
                                        setPeakIndices((prev) => {
                                            const peaks = prev[e.reference_substance];
                                            if (!peaks || peaks.length === 0) return prev;
                                            const updatedPeaks = peaks.map((peak, i) => ({
                                                ...peak,
                                                selected: false
                                            }));
                                            return {
                                                ...prev,
                                                [e.reference_substance]: updatedPeaks,
                                            };
                                        });
                                        setScalePercent(prevScalePercent => ({
                                            ...prevScalePercent,
                                            [e.reference_substance]: newValue
                                        }));
                                    }}
                                    value={typeof scalePercent[e.reference_substance] === 'number' ? scalePercent[e.reference_substance] : 0}
                                />
                            )}>
                                <InputNumber
                                    min={0}
                                    controls={false}
                                    style={{ width: '105px' }}
                                    addonAfter="%"
                                    value={scalePercent[e.reference_substance]?.toFixed(2)}
                                    onChange={newValue => {
                                        setPeakIndices((prev) => {
                                            const peaks = prev[e.reference_substance];
                                            if (!peaks || peaks.length === 0) return prev;
                                            const updatedPeaks = peaks.map((peak, i) => ({
                                                ...peak,
                                                selected: false
                                            }));
                                            return {
                                                ...prev,
                                                [e.reference_substance]: updatedPeaks,
                                            };
                                        });
                                        setScalePercent(prevScalePercent => ({
                                            ...prevScalePercent,
                                            [e.reference_substance]: newValue
                                        }));
                                    }}
                                />
                            </Popover>
                            <Button
                                icon={<UndoOutlined />}
                                onClick={() => {
                                    setPeakIndices((prev) => {
                                        const peaks = prev[e.reference_substance];
                                        const updatedPeaks = peaks.map(peak =>
                                            ({ ...peak, selected: false })
                                        );
                                        return {
                                            ...prev,
                                            [e.reference_substance]: updatedPeaks,
                                        };
                                    });
                                    setScalePercent(prevScalePercent => ({
                                        ...prevScalePercent,
                                        [e.reference_substance]: 100
                                    }));
                                    updateMarklineSeries([]);
                                    setMapFilterByML(false);
                                    setSelectedSubstanceForMapFilter(null);
                                }}
                            />
                            <Button
                                icon={<EyeOutlined />}
                                ghost={legendSelected[e.reference_substance] ? true : false}
                                type={legendSelected[e.reference_substance] ? 'primary' : 'default'}
                                onClick={() => {
                                    setLegendSelected(prev => {
                                        const newLegendSelected = { ...prev };
                                        newLegendSelected[e.reference_substance] = !newLegendSelected[e.reference_substance];
                                        return newLegendSelected;
                                    }
                                    )
                                }}
                            />
                            <Button
                                icon={<DotChartOutlined />}
                                ghost={(selectedSubstanceForMapFilter === e.reference_substance && mapFilterByML === false) ? true : false}
                                type={(selectedSubstanceForMapFilter === e.reference_substance && mapFilterByML === false) ? 'primary' : 'default'}
                                onClick={() => {
                                    clearSingleWavenumberFilter();
                                    if (selectedSubstanceForMapFilter === e.reference_substance && mapFilterByML === false) {
                                        setSelectedSubstanceForMapFilter(null);
                                    }
                                    else if (selectedSubstanceForMapFilter === e.reference_substance && mapFilterByML === true) {
                                        setMapFilterByML(false);
                                    } else if (selectedSubstanceForMapFilter !== e.reference_substance && mapFilterByML === false) {
                                        setSelectedSubstanceForMapFilter(e.reference_substance);
                                    }
                                    else if (selectedSubstanceForMapFilter !== e.reference_substance && mapFilterByML === true) {
                                        setSelectedSubstanceForMapFilter(e.reference_substance);
                                        setMapFilterByML(false);
                                    }
                                }}
                            />
                            <Button
                                disabled={!mlSubstanceArray.includes(e.reference_substance)}
                                icon={<BranchesOutlined />}
                                ghost={(selectedSubstanceForMapFilter === e.reference_substance && mapFilterByML === true) ? true : false}
                                type={(selectedSubstanceForMapFilter === e.reference_substance && mapFilterByML === true) ? 'primary' : 'default'}
                                onClick={() => {
                                    clearSingleWavenumberFilter();
                                    if (selectedSubstanceForMapFilter === e.reference_substance && mapFilterByML === false) {
                                        setMapFilterByML(true);
                                    } else if (selectedSubstanceForMapFilter === e.reference_substance && mapFilterByML === true) {
                                        setMapFilterByML(false);
                                        setSelectedSubstanceForMapFilter(null);
                                    } else if (selectedSubstanceForMapFilter !== e.reference_substance && mapFilterByML === false) {
                                        setSelectedSubstanceForMapFilter(e.reference_substance);
                                        setMapFilterByML(true);
                                    } else if (selectedSubstanceForMapFilter !== e.reference_substance && mapFilterByML === true) {
                                        setSelectedSubstanceForMapFilter(e.reference_substance);
                                    }
                                }}
                            />
                            <InputNumber addonBefore={'ΔR²'} value={r2Contributions[e.reference_substance]?.toFixed(3)} style={{ width: '115px' }} readOnly />
                        </div>
                    ))
                }
            </div>
        </>
    );
}

export default SpectraLineChart